import { getSharedStyleSheet } from './sharedStyleSheet.js';

class ThumbnailImage extends HTMLElement {
    static observedAttributes = ['src', 'hover-src', 'frame-width', 'size', 'file-type', 'hover-frame', 'duration'];

    constructor() {
        super();
        this._onCloseHandler = null;
        this.reloadCount = 0;
        this.isVisible = false;
    }

    attributeChangedCallback(attrName, oldValue, newValue) {
        if (attrName === 'hover-frame' && oldValue !== newValue) {
            this.loadAndDrawHoverscrubImageForFrameNumber(newValue);
        } else {
            let shouldRedraw = false;
            if (attrName === 'size' || attrName === 'src' || attrName === 'hover-src') shouldRedraw = true;
            this.dynamicRender(shouldRedraw);
        }
    }

    connectedCallback() {
        if (!this.shadowRoot) {
            this.attachShadow({mode: 'open'});

            // Make sure element obeys parent sizing
            this.shadowRoot.host.style.display = 'inline-block';

            // Share a single parsed stylesheet across all shadow roots
            getSharedStyleSheet().then(sheet => {
                this.shadowRoot.adoptedStyleSheets = [sheet];
            });

            this._wrapper = document.createElement('div');
            this._wrapper.classList.add('thumbnail-wrapper');
            this.shadowRoot.appendChild(this._wrapper);

            // Defer loading until it's on-screen
            if (!!this.getAttribute("lazy")) {
                const options = {
                    root: null, // Observe intersection with the viewport
                    rootMargin: '0px',
                    threshold: 0.05 // Trigger callback when 5% of the target is visible
                };

                const observer = new IntersectionObserver(callback, options);
                const self = this;

                function callback(entries, observer) {
                    entries.forEach(entry => {
                        self.isVisible = entry.isIntersecting;
                        self.dynamicRender(self.isVisible)
                    });
                }

                observer.observe(this._wrapper);
            } else {
                this.isVisible = true;
                this.dynamicRender(self.isVisible);
            }
        }

        this.render(this.isVisible);
    }

    disconnectedCallback() {
        if (this.obj) {
            this.obj.removeEventListener('mousemove', this.hoverScrub.bind(this));
            this.obj = null;
        }
    }

    hoverScrub(event) {
        if (!this.getAttribute('hover-src')) return;

        // Get cursor position
        const rect = this._canvas.getBoundingClientRect();
        const pos = event.clientX - rect.left;

        // Get frame number
        let frameNumber = Math.ceil((pos / this._canvas.clientWidth) * 100.0) - 1;
        if (frameNumber > 99) frameNumber = 99;
        if (frameNumber < 0) frameNumber = 0;

        this.loadAndDrawHoverscrubImageForFrameNumber(frameNumber);

        // Draw playhead
        this._playhead.style.display = "inline-block";
        this._playhead.style.left = frameNumber + "%";
        this._playhead.style.height = this._canvas.clientHeight + "px";
    }

    mouseLeave(event) {
        if (!this.getAttribute('hover-src')) return;

        if (this._playhead) {
            this._playhead.style.display = 'none';
        }
    }

    drawHoverImageForFrameNumber(frameNumber) {
        if (!this.hoverImage) return;

        const hoverImageWidth = this.hoverImage.naturalWidth;
        const hoverImageHeight = this.hoverImage.naturalHeight;

        let xPos = 0;
        let yPos = 0;
        let frameWidth = 0;
        let frameHeight = 0;

        // Check if it's old-style hoverscrub or new
        if (hoverImageHeight / hoverImageWidth < 0.05) {
            frameWidth = hoverImageWidth / 100;
            frameHeight = hoverImageHeight;

            xPos = frameWidth * frameNumber;
            yPos = 0;
        } else { // Row-based format
            let row = Math.floor(frameNumber / 10);
            let col = frameNumber - (row * 10);

            frameWidth = hoverImageWidth / 10;
            frameHeight = hoverImageHeight / 10;

            xPos = col * frameWidth;
            yPos = row * frameHeight;
        }

        this.calculateDimensions(frameWidth, frameHeight, this._canvas.clientWidth, this._canvas.clientHeight);

        this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
        this._ctx.drawImage(this.hoverImage, xPos, yPos, frameWidth, frameHeight, this.imageX, this.imageY, this.effectiveWidth, this.effectiveHeight);
    }

    loadAndDrawHoverscrubImageForFrameNumber(frameNumber) {
        if (!this.hoverImage) {
            this.loadImage(this.getAttribute('hover-src')).then(image => {
                this.hoverImage = image;

                this.drawHoverImageForFrameNumber(frameNumber);
            });
        } else {
            this.drawHoverImageForFrameNumber(frameNumber);
        }
    }

    render(shouldRedrawThumb = true) {
        let canvasWidth = 120;
        let canvasHeight = 68;

        const size = this.getAttribute('size');
        if (size === 'mini') {
            canvasWidth = 60;
            canvasHeight = 34;
        } else if (size === 'micro') {
            canvasWidth = 45;
            canvasHeight = 25;
        } else if (size === 'large') {
            canvasWidth = 240;
            canvasHeight = 136;
        }

        const canvas = `<canvas id="thumbnail" width="${canvasWidth}" height="${canvasHeight}"></canvas>`;

        this._wrapper.insertAdjacentHTML('beforeend', canvas);
        this._canvas = this._wrapper.querySelector('#thumbnail');
        this._ctx = this._canvas.getContext('2d');

        if (this.getAttribute('size') !== 'mini' && this.getAttribute('size') !== 'micro') {
            if (this.getAttribute('has-audio')) {
                this._wrapper.insertAdjacentHTML('afterbegin', `<div class="has-audio"></div>`);
            }

            const duration = this.getAttribute('duration');
            if (duration) {
                this._wrapper.insertAdjacentHTML('afterbegin', `<div class="file-duration">${duration}</div>`);
            }
        }

        this.dynamicRender(shouldRedrawThumb);
    }

    async dynamicRender(redrawThumb) {
        if (!this._wrapper) return;

        this._wrapper.dataset.fileType = this.getAttribute('file-type') ?? '';

        let src = this.getAttribute('src');
        const originalSource = src;

        // Show unknown doc if unspecified
        if (!src && this.getAttribute('file-type') !== 'video') {
            src = '/img/Unknown_Doc.svg';
        }

        if (this.getAttribute('size')) this._wrapper.classList.add(this.getAttribute('size'));

        if (redrawThumb) {
            let canvasWidth = 120;
            let canvasHeight = 68;

            const size = this.getAttribute('size');
            if (size === 'mini') {
                canvasWidth = 60;
                canvasHeight = 34;
            } else if (size === 'micro') {
                canvasWidth = 45;
                canvasHeight = 25;
            } else if (size === 'large') {
                canvasWidth = 240;
                canvasHeight = 136;
            }

            this._canvas.setAttribute('width', canvasWidth);
            this._canvas.setAttribute('height', canvasHeight);

            if (!this.getAttribute('hover-frame')) {
                this.loadImage(src).then(image => {
                    this.calculateDimensions(image.naturalWidth, image.naturalHeight, this._canvas.clientWidth, this._canvas.clientHeight);

                    this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
                    this._ctx.drawImage(image, this.imageX, this.imageY, this.effectiveWidth, this.effectiveHeight);
                });
            }

            if (this.getAttribute('hover-src')) {
                const playhead = `<div class="thumbnail-playhead"></div>`;
                this._wrapper.insertAdjacentHTML('afterbegin', playhead);
                this._playhead = this._wrapper.querySelector('.thumbnail-playhead');

                if (size) {
                    this._playhead.classList.add(this.size);
                }

                // Listen for mouse move
                if (!this.getAttribute('no-scrub')) {
                    this._wrapper.addEventListener('mousemove', this.hoverScrub.bind(this));
                    this._wrapper.addEventListener('mouseleave', this.mouseLeave.bind(this));
                }

                if (this.getAttribute('hover-frame')) {
                    this.loadAndDrawHoverscrubImageForFrameNumber(this.getAttribute('hover-frame'));
                }
            }
        }

        if (this.getAttribute('file-type') === 'video') {
            this._wrapper.style.backgroundColor = '#000';
        }

        // Fix race condition
        if (this.reloadCount === 0 && !originalSource) {
            window.setTimeout(() => {
                this.reloadCount++;
                this.dynamicRender(true);
            }, 250);
        }
    }

    loadImage(url) {
        return new Promise(resolve => {
            const image = new Image();
            image.addEventListener('load', () => {
                resolve(image);
            });
            image.src = url;
        });
    }

    calculateDimensions(imageWidth, imageHeight, containerWidth, containerHeight) {
        this.imageWidth = imageWidth;
        this.imageHeight = imageHeight;

        if (this.imageWidth > this.imageHeight) {
            this.effectiveWidth = containerWidth;
            this.effectiveHeight = this.effectiveWidth * (this.imageHeight / this.imageWidth);

            this.imageX = 0;
            this.imageY = (containerHeight - this.effectiveHeight) / 2;
        } else {
            this.effectiveHeight = containerHeight;
            this.effectiveWidth = this.effectiveHeight * (this.imageWidth / this.imageHeight);

            this.imageX = (this._canvas.clientWidth - this.effectiveWidth) / 2;
            this.imageY = 0;
        }

    }
}

customElements.define('thumbnail-image', ThumbnailImage);
